/**
 * Client-side image compression utility using Canvas API.
 * Resizes images to a maximum dimension while maintaining aspect ratio.
 * Used to reduce upload size before sending to the server.
 */

export interface CompressImageOptions {
  /** Maximum dimension (width or height) in pixels. Default: 2048 */
  maxDimension?: number;
  /** JPEG quality (0-1). Default: 0.85 */
  quality?: number;
  /** Output MIME type. Default: 'image/jpeg' */
  outputType?: string;
}

/**
 * Compresses an image file by resizing it to fit within maxDimension
 * and re-encoding as JPEG at the specified quality.
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns A new File with the compressed image data
 */
export async function compressImage(
  file: File,
  options: CompressImageOptions = {}
): Promise<File> {
  const {
    maxDimension = 2048,
    quality = 0.85,
    outputType = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        URL.revokeObjectURL(url);

        let { width, height } = img;

        // Only resize if image exceeds maxDimension
        if (width <= maxDimension && height <= maxDimension) {
          // Still re-encode to JPEG for consistent format
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file); // Fallback to original
            return;
          }
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              // Only use compressed version if it's actually smaller
              if (blob.size >= file.size) {
                resolve(file);
                return;
              }
              const compressedName = file.name.replace(/\.[^.]+$/, '.jpg');
              resolve(new File([blob], compressedName, {
                type: outputType,
                lastModified: file.lastModified,
              }));
            },
            outputType,
            quality
          );
          return;
        }

        // Calculate new dimensions maintaining aspect ratio
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        const newWidth = Math.round(width * ratio);
        const newHeight = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file); // Fallback to original if canvas context unavailable
          return;
        }

        // Use high-quality downsampling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file); // Fallback to original
              return;
            }
            const compressedName = file.name.replace(/\.[^.]+$/, '.jpg');
            resolve(new File([blob], compressedName, {
              type: outputType,
              lastModified: file.lastModified,
            }));
          },
          outputType,
          quality
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
}
